/**
 * Page Transition Component
 *
 * Provides smooth visual transitions between page routes using blur effects.
 * Detects route changes and temporarily applies transition styles for enhanced UX.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/PageTransition.css';

/**
 * PageTransition - Wraps content with route transition effects
 *
 * Features:
 * - Detects route changes using React Router location
 * - Applies blur transition effect during route changes
 * - Automatically cleans up timeouts to prevent memory leaks
 * - Smooth 100ms transition duration
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to wrap with transition effects
 * @returns {JSX.Element} Wrapped content with transition styling
 */
const PageTransition = ({ children }) => {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevLocationRef = useRef(location);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (location.pathname !== prevLocationRef.current.pathname) {
      setIsTransitioning(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
      }, 100);

      prevLocationRef.current = location;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`page-transition-container ${isTransitioning ? 'blur-transition' : ''}`}>
      <div className="page-content">
        {children}
      </div>
    </div>
  );
};

export default PageTransition;